import React, { useState } from 'react';
import { Button, Tabs } from 'antd';
import { getUserTimeZone } from '@/common/utils/utils';
import { resolveIana } from '@/pages/Logiblocks/dateTimeUtils';
import { DisplayTimeSelection } from '@/pages/Shipments/MapExternalView/types';
import PrimaryButton from './PrimaryButton';
import SpecificDatesEditor from './SpecificDatesEditor';
import DayOfWeekEditor from './DayOfWeekEditor';
import { DateSetValue, DateWindowEntry, DayWindowEntry } from './dateSetTypes';
import './LogiblocksDTDPicker.scss';

type Tab = 'specific-date' | 'day-of-week';

interface LogiblocksDatePickerPanelProps {
  /** The set being edited — either kind, never both at once. */
  value: DateSetValue;
  /** Receives the active tab's entries and the resolved timezone. */
  onSubmit: (value: DateSetValue) => void;
  /** "Submit" for a fresh selection; "Done" when this panel is a drill-in
   * from the list-edit panel and just returns without saving. */
  footerVerb: 'Submit' | 'Done';
  /** Omit to hide "Save as a list" (the drill-in already edits a saved list). */
  onSaveAsList?: (value: DateSetValue) => void;
  disabled?: boolean;
}

/** The selector's starting state: the browser/account timezone (labelled "My
 * local time") when the value carries none or already holds that zone, else
 * the stored zone. Either way it resolves to a concrete IANA name. */
function initialTimezoneSelection(
  ianaTimezone: string | undefined
): DisplayTimeSelection {
  if (!ianaTimezone || ianaTimezone === getUserTimeZone().name) {
    return { type: 'preset', value: 'my_local_time' };
  }
  return { type: 'timezone', value: ianaTimezone };
}

/** The calendar / day-of-week picker: two tabs of chips and a timezone, with
 * Submit/Done and an optional "Save as a list". */
const LogiblocksDatePickerPanel: React.FC<LogiblocksDatePickerPanelProps> = ({
  value,
  onSubmit,
  footerVerb,
  onSaveAsList,
  disabled = false,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>(
    value.entries.kind === 'daysOfWeek' ? 'day-of-week' : 'specific-date'
  );
  const [dateEntries, setDateEntries] = useState<DateWindowEntry[]>(
    value.entries.kind === 'dates' ? value.entries.entries : []
  );
  const [dayEntries, setDayEntries] = useState<DayWindowEntry[]>(
    value.entries.kind === 'daysOfWeek' ? value.entries.entries : []
  );
  const [timezoneValue, setTimezoneValue] = useState<DisplayTimeSelection>(
    initialTimezoneSelection(value.ianaTimezone)
  );

  const activeCount =
    activeTab === 'specific-date' ? dateEntries.length : dayEntries.length;

  const currentValue = (): DateSetValue => ({
    entries:
      activeTab === 'specific-date'
        ? { kind: 'dates', entries: dateEntries }
        : { kind: 'daysOfWeek', entries: dayEntries },
    ianaTimezone: resolveIana(timezoneValue),
    operator: value.operator,
  });

  const items = [
    {
      key: 'specific-date',
      label: 'Specific date(s)',
      children: (
        <SpecificDatesEditor
          entries={dateEntries}
          onChange={setDateEntries}
          timezoneValue={timezoneValue}
          onTimezoneChange={setTimezoneValue}
          disabled={disabled}
        />
      ),
    },
    {
      key: 'day-of-week',
      label: 'Day of the week',
      children: (
        <DayOfWeekEditor
          entries={dayEntries}
          onChange={setDayEntries}
          timezoneValue={timezoneValue}
          onTimezoneChange={setTimezoneValue}
          disabled={disabled}
        />
      ),
    },
  ];

  return (
    <div className='lb-date-picker-panel'>
      <Tabs
        activeKey={activeTab}
        onChange={(key) =>
          setActiveTab(key === 'day-of-week' ? 'day-of-week' : 'specific-date')
        }
        items={items}
        className='lb-date-picker-panel__tabs'
      />

      <div className='lb-date-picker-panel__footer'>
        {onSaveAsList && activeCount > 0 && (
          <Button type='text' onClick={() => onSaveAsList(currentValue())}>
            Save as a list
          </Button>
        )}
        <PrimaryButton
          disabled={activeCount === 0}
          size='small'
          className='lb-date-picker-panel__done-btn'
          onClick={() => onSubmit(currentValue())}
        >
          {footerVerb}
        </PrimaryButton>
      </div>
    </div>
  );
};

export default LogiblocksDatePickerPanel;
